import React, { useState } from 'react';
import { Folder, FolderOpen } from 'lucide-react';

type TreeNodeData = {
  name: string;
  children?: TreeNodeData[];
};

function TreeNode({ name, children }: TreeNodeData) {
  const [open, setOpen] = useState(false);
  const hasChildren = Array.isArray(children) && children.length > 0;

  return (
    <div className="pl-3 sm:pl-4 md:pl-6 break-words">
      <div
        className={`cursor-pointer flex items-center gap-2 py-1 ${
          hasChildren
            ? 'hover:bg-blue-50 dark:hover:bg-gray-700 rounded-md px-1'
            : ''
        }`}
        onClick={() => hasChildren && setOpen(!open)}
      >
        {hasChildren ? (
          open ? (
            <FolderOpen size={18} className="text-blue-600 shrink-0" />
          ) : (
            <Folder size={18} className="text-blue-600 shrink-0" />
          )
        ) : (
          <span className="w-[18px]" />
        )}
        <span className="font-mono text-[13px] sm:text-sm truncate">
          {name}
        </span>
      </div>

      {open && hasChildren && (
        <div className="border-l border-gray-300 ml-3 sm:ml-4 pl-2 sm:pl-3 overflow-x-auto">
          {children.map((child, i) => (
            <TreeNode key={i} {...child} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DatasetTree() {
  const timestampPlaceholder = '<timestamp>';

  const metadataFiles = [
    'basler_metadata.csv',
    'battery_logs.csv',
    'cmd_velocity_left.csv',
    'cmd_velocity_right.csv',
    'cmd_velocity.csv',
    'current_left.csv',
    'current_right.csv',
    'dps310.csv',
    'meteo_data.csv',
    'snow_data.csv',
    'velocity_left.csv',
    'velocity_right.csv',
    'vn100_pressure.csv',
    'vn100_temperature.csv',
    'voltage_left.csv',
    'voltage_right.csv',
  ];

  const treeData: TreeNodeData[] = [
    {
      name: 'YYYY-MM-DD',
      children: [
        {
          name: 'blue_YYYY-MM-DD-HH-MM',
          children: [
            {
              name: 'audio_left',
              children: [{ name: `${timestampPlaceholder}.wav` }],
            },
            {
              name: 'audio_right',
              children: [{ name: `${timestampPlaceholder}.wav` }],
            },
            {
              name: 'basler',
              children: [{ name: `${timestampPlaceholder}.png` }],
            },
            {
              name: 'calib',
              children: [
                { name: 'basler.json' },
                { name: 'imu.json' },
                { name: 'transforms.json' },
                { name: 'zedx_left.json' },
                { name: 'zedx_right.json' },
              ],
            },
            {
              name: 'leishen',
              children: [{ name: `${timestampPlaceholder}.bin` }],
            },
            {
              name: 'metadata',
              children: metadataFiles.map((f) => ({ name: f })),
            },
            {
              name: 'navtech',
              children: [{ name: `${timestampPlaceholder}.png` }],
            },
            {
              name: 'robosense',
              children: [{ name: `${timestampPlaceholder}.bin` }],
            },
            {
              name: 'zedx_left',
              children: [{ name: `${timestampPlaceholder}.png` }],
            },
            {
              name: 'zedx_right',
              children: [{ name: `${timestampPlaceholder}.png` }],
            },
            { name: 'gt.txt' },
            { name: 'gt_covariance.csv' },
            { name: 'odom.csv' },
            { name: 'vectornav.csv' },
            { name: 'emlid_metadata.csv' },
            { name: 'xsens.csv' },
          ],
        },
        { name: 'green_YYYY-MM-DD-HH-MM' },
        { name: 'magenta_YYYY-MM-DD-HH-MM' },
        { name: 'orange_YYYY-MM-DD-HH-MM' },
        { name: 'red_YYYY-MM-DD-HH-MM' },
        { name: 'yellow_YYYY-MM-DD-HH-MM' },
      ],
    },
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 w-full max-w-full md:max-w-5xl mx-auto text-sm overflow-x-auto">
      <TreeNode name="FoMo Dataset" children={treeData} />
    </div>
  );
}
